import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User } from "lucide-react";
import { api, titleUrl } from "@/lib/api";

export function SearchBox({ big = false, onDone }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const box = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) return setRes(null);
    const id = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => setRes({ error: true })), 220);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    const h = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (url) => { setOpen(false); setQ(""); onDone?.(); nav(url); };
  const submit = (e) => { e.preventDefault(); if (q.trim()) go(`/movies?q=${encodeURIComponent(q.trim())}&type=`); };

  return (
    <div ref={box} className="relative w-full">
      <form onSubmit={submit} className={`flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 backdrop-blur focus-within:border-amber-400/70 transition-colors ${big ? "py-3.5" : "py-2"}`}>
        <Search className="h-4 w-4 text-slate-400" />
        <input data-testid={big ? "hero-search-input" : "header-search-input"} value={q} onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }} aria-label="Search movies, series, actors"
          placeholder={big ? "Search a movie, series, actor, director, genre, year or platform…" : "Search titles, people…"}
          className={`w-full bg-transparent outline-none placeholder:text-slate-500 ${big ? "text-base" : "text-sm"}`} />
      </form>
      {open && res && (
        <div data-testid="search-results" className="absolute left-0 right-0 z-50 mt-2 max-h-[70vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#12121A]/95 p-2 shadow-2xl backdrop-blur-xl">
          {res.error && <p className="p-3 text-sm text-red-400">Search is unavailable right now.</p>}
          {res.titles?.map((t) => (
            <button key={t.id} data-testid={`search-result-${t.slug}`} onClick={() => go(titleUrl(t))} className="flex w-full items-center gap-3 rounded-xl p-2 text-left hover:bg-white/5">
              <img src={t.poster?.replace("/w500/", "/w92/")} alt="" className="h-14 w-10 rounded object-cover bg-white/5" />
              <div><p className="text-sm font-semibold">{t.title}</p><p className="text-xs text-slate-500">{t.year} · {t.type === "tv" ? "Series" : t.type === "anime" ? "Anime" : "Movie"} · {t.genres?.slice(0, 2).join(", ")}</p></div>
            </button>
          ))}
          {res.people?.length > 0 && <p className="px-2 pt-2 eyebrow">People</p>}
          {res.people?.map((p) => (
            <button key={p} onClick={() => go(`/movies?q=${encodeURIComponent(p)}&type=`)} className="flex w-full items-center gap-2 rounded-xl p-2 text-left text-sm hover:bg-white/5"><User className="h-4 w-4 text-purple-400" />{p}</button>
          ))}
          {res.posts?.length > 0 && <p className="px-2 pt-2 eyebrow">Community</p>}
          {res.posts?.map((p) => <button key={p.id} onClick={() => go(`/community/post/${p.id}`)} className="block w-full rounded-xl p-2 text-left text-sm hover:bg-white/5">{p.title}</button>)}
          {!res.error && !res.titles?.length && !res.people?.length && !res.posts?.length && <p className="p-3 text-sm text-slate-500">No matches for “{q}”.</p>}
        </div>
      )}
    </div>
  );
}
